import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import { PRODUCT_INFO_SECTION, trackProductInfoSectionSelected } from 'data-hooks/useEvents/pdpInteractionEvents';
import PDPDeferredReviews from '../PDPDeferredReviews';
import usePDPInfoSections from '../PDPInfoSections/usePDPInfoSections';

// Rendered closed from the first render, so the lazy chunk never suspends on a tap
const AnimateHeight = loadable(() => import('react-animate-height'));
const DURATION = 300;
// Starts fast and settles slowly, where the default ease stops abruptly
const EASING = 'cubic-bezier(0.2, 0, 0, 1)';

// Mobile counterpart to PDPInfoTabs: four tab labels do not fit on a phone, so
// the same sections stack as expandable rows. Panels stay mounted while closed
// so a section that loads its own media is not torn down on every toggle.
const PDPInfoAccordionInner = () => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      stacked: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const sections = usePDPInfoSections(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      id: "pdp-info-reviews",
      section: PRODUCT_INFO_SECTION.REVIEWS,
      label: "Reviews",
      content: <PDPDeferredReviews isLayoutRevamp={true} />
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== sections) {
    t2 = [...sections, t1];
    $[2] = sections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const items = t2;
  const [openId, setOpenId] = useState(null);
  const stopHoldRef = useRef(null);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => stopHoldRef.current?.();
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== items || $[7] !== openId) {
    const holdAgainstCollapse = panels => {
      const totalHeight = () => panels.reduce(_temp, 0);
      let lastHeight = totalHeight();
      const observer = new ResizeObserver(() => {
        const height = totalHeight();
        window.scrollBy(0, height - lastHeight);
        lastHeight = height;
      });
      const timer = setTimeout(() => stopHoldRef.current?.(), DURATION + 100);
      panels.forEach(panel_0 => observer.observe(panel_0));
      stopHoldRef.current = () => {
        observer.disconnect();
        clearTimeout(timer);
        stopHoldRef.current = null;
      };
    };
    const toggle = (t6, index) => {
      const {
        id,
        section
      } = t6;
      stopHoldRef.current?.();
      const panelsAbove = items.slice(0, index).map(_temp2).filter(Boolean);
      if (panelsAbove.some(_temp3)) {
        holdAgainstCollapse(panelsAbove);
      }
      const isOpening = openId !== id;
      setOpenId(isOpening ? id : null);
      if (isOpening) {
        trackProductInfoSectionSelected(section);
      }
    };
    t5 = <div className="mt-8" data-xc="PDPInfoAccordion">{items.map((item_0, index_0) => {
        const {
          id: id_0,
          label,
          content
        } = item_0;
        const isOpen = openId === id_0;
        return <div key={id_0} className="border-t border-gray-light7"><button type="button" aria-expanded={isOpen} aria-controls={`${id_0}-panel`} className="bg-transparent border-0 cursor-pointer flex items-center justify-between py-4 px-0 text-left w-full" onClick={() => toggle(item_0, index_0)}><span className="text-base font-bold text-gray">{label}</span><span aria-hidden="true" className="relative shrink-0 w-3.5 h-3.5"><span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-gray-light1" /><span className={classNames("absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-light1 transition-transform duration-300 ease-out", {
                "rotate-90": isOpen
              })} /></span></button><AnimateHeight id={`${id_0}-panel`} animateOpacity={true} duration={DURATION} easing={EASING} height={isOpen ? "auto" : 0}><div className="pb-6">{content}</div></AnimateHeight></div>;
      })}</div>;
    $[6] = items;
    $[7] = openId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
const PDPInfoAccordion = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SuspenseWithLoader><PDPInfoAccordionInner /></SuspenseWithLoader>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default PDPInfoAccordion;
function _temp(sum, panel) {
  return sum + panel.offsetHeight;
}
function _temp2(item) {
  return document.getElementById(`${item.id}-panel`);
}
function _temp3(panel_1) {
  return panel_1.offsetHeight > 0;
}